/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @nx/enforce-module-boundaries */
import { createClient } from '@acordocertobr/reneg-offers-hub';
import { handleRefreshToken, shouldRefreshToken, clearACLocalData } from '@libs/acp/utils/handleRefreshToken';

import { jwtDecode } from 'jwt-decode';

import { getAnonymousID, getSessionID } from 'redline-client-sdk';
import { useUserStore } from '@libs/acp/store/user/useUserStore';
import type { AccessToken } from '@libs/acp/store/user/types';

const apiService = createClient({
  development: import.meta.env.VITE_ENV === 'dev',
  // Uncomment for local testing with api
  // customUrl: 'http://localhost:8080'
});

apiService.interceptors.request.use(async (req) => {
  req.headers.set('Content-Type', 'application/json');
  req.headers.set('Content-Type', 'application/json');
  req.headers.set('x-anonymous-id', getAnonymousID());
  req.headers.set('x-session-id', getSessionID());

  const isPublicRequest = req.url.includes('/public');

  const tokenData = useUserStore.getState().authenticatedUser.acp.auth;
  const token = tokenData?.access_token;
  const refreshToken = tokenData.refresh_token;

  const decodedAccessToken: AccessToken = token ? jwtDecode(token) : ({} as AccessToken);

  const origin = decodedAccessToken.origin || '';

  const isNCADCheckout = origin === 'ncad';
  const isAtendeSession = origin === 'atende';

  if (!isPublicRequest && (!token || (!isNCADCheckout && !isAtendeSession && !refreshToken))) {
    handleLogout();
  }

  const updatedToken = await getUpdatedToken(token, refreshToken).catch(handleLogout);
  req.headers.set('X-Auth-Token', updatedToken || '');
  return req;
});

// Response interceptor to remove API wrapper
apiService.interceptors.response.use(async (res) => {
  const isProxy = res.url.includes('proxy');

  if (isProxy) return res;

  // Check for HTTP errors first (500, 404, etc.)
  if (!res.ok) {
    let jsonRes;

    try {
      const clonedRes = res.clone();
      jsonRes = await clonedRes.json();
    } catch {
      // If response is not JSON, we can ignore this error and use statusText
    }

    const message = jsonRes?.message ? `HTTP ${res.status}: ${jsonRes.message}` : `HTTP ${res.status}: ${res.statusText}`;

    const error = new Error(message);
    (error as any).status = res.status;
    (error as any).statusText = res.statusText;
    throw error;
  }

  // Clone the response to avoid consuming the original stream
  const clonedRes = res.clone();
  const jsonRes = await clonedRes.json();

  const err = jsonRes?.['error'];
  const data = jsonRes?.['data'] || {};

  if (err) {
    const error = new Error(jsonRes?.['message'] || 'API Error');
    (error as any).errorSlug = jsonRes?.['error_slug'];
    (error as any).errorCode = jsonRes?.['error_code'];
    (error as any).fields = jsonRes?.['fields'];

    throw error;
  }

  // Create a new Response with unwrapped data
  return new Response(JSON.stringify(data), {
    status: res.status,
    statusText: res.statusText,
    headers: res.headers,
  });
});

async function getUpdatedToken(currentToken: string, refreshToken: string) {
  const shouldRefreshTokenBefore = await shouldRefreshToken();
  if (!shouldRefreshTokenBefore) return currentToken;
  const response = await handleRefreshToken({
    refreshToken: refreshToken || '',
  });
  return response?.access_token || currentToken;
}

// TODO get from ACP
function handleLogout() {
  clearACLocalData();
  window.location.href = '/login';
}

export default apiService;
